// Vendors
import React from 'react';
import { FormattedMessage } from 'react-intl';
// Constants
import { DOWNLOAD_LINK_PROPS } from '../../constants/contract.constants';
import { SCROLL_DOWN_MESSAGE_PROPS } from './constants/pre-contract.view.constants';
// Components
import {
  ButtonComponent,
  MessageComponent,
  ParagraphComponent,
  SelectorRowComponent,
} from '@openbank/ziniaweb-ui-components';
import { ContractViewerComponent } from 'containers/contract/components/viewer/contract-viewer.component';
import { FinancialDataComponent } from '../../components/financial-data/financial-data.component';
// Configurations
import { getPreContractFinancialConfig } from './configurations/pre-contract.configurations';
// Hooks
import { usePreContractHook } from './hooks/pre-contract.view.hook';
import { useContractContainerHook } from '../../hooks/contract.hook';
// Styles
import {
  ConsentsContainerStyled,
  PreContractComponentStyled,
  PreContractPDFViewerAreaStyled,
  PreContractPDFViewerDocumentAreaStyled,
  PreContractPDFAndConsentsAreaStyled,
  PreContractScrollDownMessageStyled,
} from './pre-contract.view.styled';
// Translations
import {
  CONSENT,
  DOWNLOAD_LINK_PRECONTRACT,
  DOWNLOAD_PRECONTRACT_INFORMATION,
  SCROLL_DOWN_MESSAGE,
} from './translations/pre-contract.view.translations';
// Utils
import { getConsentProps } from '../../utils/contract.utils';

const PreContractView = (): React.JSX.Element => {
  const {
    screenDetails,
    consents,
    isPreContractRead,
    isDocumentRendered,
    handleCheckConsent,
    handleDownloadPdfButtonClickTracking,
    handlePreContractRead,
    handleSetPreContractualDocumentRenderedEvent,
  } = usePreContractHook();

  const { accounts, financialData, handleDownloadDocument, formatMessage } =
    useContractContainerHook();

  return (
    <PreContractComponentStyled>
      <PreContractPDFAndConsentsAreaStyled>
        <PreContractPDFViewerAreaStyled>
          <PreContractPDFViewerDocumentAreaStyled>
            <ContractViewerComponent
              pdfUrl={screenDetails?.document?.byteCode}
              onBottomReach={handlePreContractRead}
              onRenderComplete={handleSetPreContractualDocumentRenderedEvent}
            />
          </PreContractPDFViewerDocumentAreaStyled>
        </PreContractPDFViewerAreaStyled>
        <ConsentsContainerStyled>
          <ButtonComponent
            {...DOWNLOAD_LINK_PROPS}
            disabled={!isDocumentRendered}
            onClick={evt => {
              evt.preventDefault();
              if (screenDetails?.document.byteCode) {
                handleDownloadPdfButtonClickTracking();
                handleDownloadDocument(
                  DOWNLOAD_LINK_PRECONTRACT,
                  screenDetails?.document?.byteCode as Blob
                );
              }
            }}
          >
            <FormattedMessage id={DOWNLOAD_PRECONTRACT_INFORMATION} />
          </ButtonComponent>
          {!isPreContractRead && (
            <PreContractScrollDownMessageStyled>
              <MessageComponent {...SCROLL_DOWN_MESSAGE_PROPS}>
                <ParagraphComponent>
                  <FormattedMessage id={SCROLL_DOWN_MESSAGE} />
                </ParagraphComponent>
              </MessageComponent>
            </PreContractScrollDownMessageStyled>
          )}
          {consents &&
            consents.map((consent, index) => (
              <SelectorRowComponent
                key={index}
                {...getConsentProps({ CONSENT, formatMessage, type: consent.type })}
                onToggleChange={() => {
                  handleCheckConsent(consent.type);
                }}
                onCheckboxChange={() => {
                  handleCheckConsent(consent.type);
                }}
                selected={consent.checked}
                disabled={!isPreContractRead || !isDocumentRendered}
              />
            ))}
        </ConsentsContainerStyled>
      </PreContractPDFAndConsentsAreaStyled>
      <FinancialDataComponent {...getPreContractFinancialConfig({ accounts, financialData })} />
    </PreContractComponentStyled>
  );
};

export { PreContractView };
